<template>
  <div
    class="form-color-picker"
    :class="{
      'is-invalid': state === false,
      disabled: $attrs.disabled === ''
    }"
  >
    <BFormGroup
      :disabled="$attrs.disabled"
      :label="label"
      :label-for="$attrs.id"
      :invalid-feedback="errorMessage"
      :label-class="{
        'is-invalid': state === false
      }"
    >
      <Popper
        trigger="clickToOpen"
        :options="popperOptions"
        :disabled="$attrs.disabled"
        :visible-arrow="false"
      >
        <div class="popper">
          <Sketch
            :value="value || '#000000'"
            disable-alpha
            disable-fields
            @input="input"
          />
        </div>

        <template slot="reference">
          <BInputGroup>
            <BFormInput
              :value="value"
              :state="state"
              :aria-label="label || $attrs.placeholder"
              v-bind="$attrs"
              v-on="listeners"
              @input="input"
            />

            <template v-slot:prepend>
              <div
                class="form-color-picker-box"
                :style="{ backgroundColor: colorBox, borderColor: colorBorderBox}"
              >
&nbsp;
              </div>
            </template>
          </BInputGroup>
        </template>
      </Popper>
    </BFormGroup>
  </div>
</template>

<script>
import Popper from 'vue-popperjs'
import isHexColor from 'validator/es/lib/isHexColor'
import {Sketch} from 'vue-color'
import {BFormGroup} from 'bootstrap-vue/src/components/form-group/form-group'
import {BInputGroup} from 'bootstrap-vue/src/components/input-group/input-group'
import {BFormInput} from 'bootstrap-vue/src/components/form-input/form-input'

export default {
  components: {
    BFormGroup,
    BInputGroup,
    BFormInput,
    Sketch,
    Popper,
  },
  inheritAttrs: false,
  props: {
    /**
     * The current value of the textarea. Result will always be a string.
     */
    value: {
      type: [String, Number],
      default: undefined,
    },

    /**
     * Text to place in the label/legend of the form group
     */
    label: {
      type: String,
      default: undefined,
    },

    /**
     * Controls the validation state appearance of the component. 'true' for valid, 'false' or 'null' for no validation state
     */
    isInvalid: {
      type: Boolean,
      default: false,
    },

    /**
     * Text to show when the form group has an invalid state
     */
    errorMessage: {
      type: String,
      default: undefined,
    },
  },

  data: () => ({
    popperOptions: {
      placement: 'bottom-start',
      modifiers: {
        flip: {
          behavior: ['bottom-start', 'top-start'],
        },
      },
    },
  }),

  computed: {
    state () {
      if (this.errorMessage && this.errorMessage.length > 0) {
        return false
      } else if (this.isInvalid) {
        return false
      } else {
        return null
      }
    },
    listeners () {
      const {input, ...listeners} = this.$listeners
      return listeners
    },
    colorBox () {
      if (this.value && this.value[0] === '#' && isHexColor(this.value)) {
        return this.value
      } else {
        return '#000000'
      }
    },
    colorBorderBox () {
      if (
        this.colorBox.toLowerCase() === '#fff' ||
        this.colorBox.toLowerCase() === '#ffffff'
      ) {
        return 'inherit'
      } else {
        return this.state === false ? this.colorBox : `${this.colorBox} !important`
      }
    },
  },

  methods: {
    input (color) {
      const hexColor = typeof color === 'object' ? color.hex : color
      this.$emit('input', hexColor)
    },
  },
}
</script>

<style lang="scss">


.form-color-picker {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;

  &.is-invalid {
    .form-color-picker-box {
      border-color: $red-500 !important;
    }
  }

  &.disabled {
    pointer-events: none;
  }

  .form-control {
    border-left: none !important;

    &:disabled {
      pointer-events: none;
    }

    &:hover:not(.is-invalid):not(:invalid) {
      border-color: transparent;
    }

    &:focus {
      border-color: $gray-200;
      padding: $input-padding-y $input-padding-x;
      border-width: 1px;
    }

    &[readonly] {
      ~ .form-color-picker-box {
        background-color: $gray-000;
      }
    }
  }

  &:hover {
    .form-control,
    .form-control:focus {
      border-color: $gray-700 !important;
    }

    .form-color-picker-box {
      border-color: $gray-700 !important;
      color: $gray-800;
    }
  }

  .input-group-prepend {
    margin-right: 0;
  }

  .form-color-picker-box {
    color: $gray-300;
    border: 1px solid $gray-200 !important;
    padding: 0 1rem;
    width: 40px;

    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    border-top-left-radius: $btn-border-radius;
    border-bottom-left-radius: $btn-border-radius;

    @include transition($input-transition);

    &:focus,
    &.focus {
      outline: 0;
    }

    // Disabled comes first so active can properly restyle
    &.disabled,
    &:disabled {
      color: $gray-300;
      background-color: $gray-100;
      border-color: $gray-200;
      cursor: default;
    }
  }

  //
  .vc-sketch {
    width: 256px;
    border-radius: 2px;
    box-shadow: 0 2px 8px 0 rgba(37, 37, 37, 0.16), 0 0 4px 0 rgba(37, 37, 37, 0.08);
    padding: 0;
  }

  .vc-sketch-color-wrap {
    display: none;
  }

  .vc-sketch-hue-wrap {
    height: 12px;
    margin: 0 1rem;
  }

  .vc-hue--horizontal {
    padding: 0 0.5em 0 0.1rem;
    border-radius: 40px !important;
  }

  .vc-hue-picker {
    height: 8px;
    width: 8px;
    border-radius: 50%;
    box-shadow: 0 0 0 1.5px #fff, inset 0 0 1px 1px rgba(0,0,0,.3), 0 0 1px 2px rgba(0,0,0,.4);
    margin-top: 2px;
    background: transparent;
  }

  .vc-sketch-presets {
    padding: 1rem;
    margin: 0;
  }

  .vc-sketch-presets-color {
    height: 20px;
    width: 20px;
    margin: 0 9px 9px 0;

    &:nth-child(8),
    &:nth-child(16) {
      margin-right: 0;
    }
  }

  .vc-sketch-sliders {
    padding: 1rem 0;
  }

  .vc-saturation-circle {
    width: 8px;
    height: 8px;
    box-shadow: 0 0 0 2px #fff, inset 0 0 1px 1px rgba(0,0,0,.3), 0 0 1px 2px rgba(0,0,0,.4);
  }

  .popper {
    background: none;
    border: none;
    padding: 0;
    box-shadow: none;
  }
}
</style>
